<template>
  <div class="values-checklist">
    <div class="values-checklist__search">
      <font-awesome-icon :icon="['fal', 'magnifying-glass']" class="values-checklist__search-icon" />
      <b-form-input
        v-model="term"
        type="search"
        size="sm"
        :placeholder="`Search ${field.label.toLowerCase()}`"
        :aria-label="`Search ${field.label.toLowerCase()}`"
      />
    </div>

    <div class="values-checklist__meta">
      <span>{{ selected.length }} of {{ options.length }} selected</span>
      <span class="values-checklist__meta-actions">
        <b-button variant="link" size="sm" data-test="select-all" @click="selectVisible">Select all</b-button>
        <b-button variant="link" size="sm" data-test="clear" @click="clearVisible">Clear</b-button>
      </span>
    </div>

    <div class="values-checklist__list" role="group" :aria-label="`${field.label} values`">
      <label
        v-for="opt in visibleOptions"
        :key="opt.value"
        class="values-checklist__option"
        :class="{ 'is-checked': isChecked(opt.value), 'is-stale': opt.stale }"
      >
        <b-form-checkbox :checked="isChecked(opt.value)" class="values-checklist__checkbox" @change="toggle(opt.value)">
          <span data-test="option-label" class="values-checklist__label">{{ opt.label }}</span>
          <span v-if="opt.stale" data-test="stale-note" class="values-checklist__stale-note">no longer exists</span>
        </b-form-checkbox>
        <span
          v-if="opt.count !== null && opt.count !== undefined"
          data-test="option-count"
          class="values-checklist__count"
        >
          {{ toNumber(opt.count) }}
        </span>
      </label>
      <small v-if="!visibleOptions.length" class="values-checklist__empty"
        >No {{ field.label.toLowerCase() }} match “{{ term }}”</small
      >
    </div>

    <div class="values-checklist__footer">
      <b-button variant="outline-secondary" size="sm" data-test="cancel" @click="$emit('cancel')">Cancel</b-button>
      <b-button variant="primary" size="sm" data-test="done" @click="$emit('done', selected)">Done</b-button>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { toNumber } from '@/utils/common';

export default {
  name: 'AudienceValuesChecklist',
  props: {
    field: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    staleValues: { type: Array, default: () => [] },
  },
  emits: ['done', 'cancel'],
  setup(props) {
    const term = ref('');
    const selected = ref([...props.value]);

    const options = computed(() => [
      ...props.staleValues.map(v => ({ value: String(v), label: String(v), count: null, stale: true })),
      ...(props.field.values || []).map(v => ({
        value: String(v.value),
        label: v.label,
        count: v.count,
        stale: false,
      })),
    ]);

    const visibleOptions = computed(() => {
      const t = term.value.trim().toLowerCase();
      return t ? options.value.filter(o => o.label.toLowerCase().includes(t)) : options.value;
    });

    const isChecked = value => selected.value.includes(value);

    const ordered = values => options.value.map(o => o.value).filter(v => values.includes(v));

    const toggle = value => {
      selected.value = isChecked(value) ? selected.value.filter(v => v !== value) : ordered([...selected.value, value]);
    };

    const selectVisible = () => {
      selected.value = ordered([...new Set([...selected.value, ...visibleOptions.value.map(o => o.value)])]);
    };

    const clearVisible = () => {
      const visible = new Set(visibleOptions.value.map(o => o.value));
      selected.value = selected.value.filter(v => !visible.has(v));
    };

    return { term, selected, options, visibleOptions, isChecked, toggle, selectVisible, clearVisible, toNumber };
  },
};
</script>

<style scoped lang="scss">
.values-checklist {
  display: flex;
  flex-direction: column;
  width: 27.5rem;
  max-width: calc(100vw - 2rem);
}

.values-checklist__search {
  align-items: center;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.values-checklist__search-icon {
  color: var(--palette-color-default-lighten-30);
}

.values-checklist__meta {
  align-items: center;
  color: var(--palette-color-default-lighten-20);
  display: flex;
  font-size: 0.8125rem;
  justify-content: space-between;
  padding: 0.5rem 1rem 0.25rem;
}

.values-checklist__meta-actions .btn {
  font-size: 0.8125rem;
  padding: 0 0 0 0.75rem;
}

.values-checklist__list {
  max-height: 18rem;
  overflow-y: auto;
}

.values-checklist__option {
  align-items: center;
  cursor: pointer;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  margin: 0;
  padding: 0.625rem 1rem;

  &.is-checked {
    background: var(--palette-color-core-1-lighten-90);
  }
}

.values-checklist__checkbox {
  flex: 1;
}

.values-checklist__option.is-stale .values-checklist__label {
  text-decoration: line-through;
}

.values-checklist__stale-note {
  color: var(--palette-color-default-lighten-20);
  font-size: 0.75rem;
  margin-left: 0.5rem;
}

.values-checklist__count {
  color: var(--palette-color-default-lighten-20);
  font-size: 0.8125rem;
}

.values-checklist__empty {
  color: var(--palette-color-default-lighten-30);
  display: block;
  padding: 0.75rem 1rem;
}

.values-checklist__footer {
  background: var(--palette-color-default-lighten-95);
  border-top: 1px solid var(--palette-color-default-lighten-90);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
}
</style>
